import { SHORTS_FIRST_FRAME_EVENT } from '~/utils/shortsEvents';

/**
 * GTM — deferred global loader.
 *
 * Loads Google Tag Manager AFTER hydration so it stays off the critical render
 * path, while GUARANTEEING it always loads on every pageview.
 *
 * Why a global plugin (not the `useGtm()` composable): a previous attempt moved
 * GTM loading into `useGtm()`, which is only called from the contact form
 * (`useContactFormActions.ts`). As a result GTM — and the GA / Facebook tags
 * inside the container — only loaded when a user opened that form, so normal
 * pageviews never loaded GTM at all. Loading here, globally, restores
 * load-on-every-page while keeping the defer.
 *
 * Why a guaranteed timeout (not `requestIdleCallback` alone): `requestIdleCallback`
 * can be starved indefinitely in background / inactive tabs, which made GTM fail
 * to load for those sessions. The trigger resolves on the FIRST of an idle
 * callback OR a fixed timeout, so it always fires shortly after hydration.
 *
 * SHORTS entry pages defer further. The container (+ the GA4/Facebook/Hotjar
 * scripts it injects, ~570KB across other origins) competes for bandwidth with
 * the entry video at the TCP level — priorities can't arbitrate across
 * connections, so on slow networks the tags directly delay the first frame.
 * There the trigger is the FIRST of: the video's first frame, the user's first
 * interaction, or a hard cap. Events fired meanwhile queue in the dataLayer
 * and flush when GTM lands; an exit BEFORE it lands sends a minimal GA4
 * pageview via sendBeacon so fast bounces still count (see beacon note).
 *
 * A Promise trigger (rather than the string `'onNuxtReady'`) is used so we can
 * keep `warmupStrategy: false` — the string trigger forces a `preload` warmup,
 * which re-introduces the "preloaded but unused" warning and partially defeats
 * the defer.
 */

export { SHORTS_FIRST_FRAME_EVENT } from '~/utils/shortsEvents';

const SHORTS_EXTRA_DEFER_CAP_MS = 5000;
const DEFAULT_DEFER_CAP_MS = 2000;

export default defineNuxtPlugin(() => {
	// Never load GTM in unit/component tests.
	if (import.meta.env.VITEST || import.meta.env.MODE === 'test') return;

	const config = useRuntimeConfig().public;
	const gtmId = config.gtmId as string;
	if (!gtmId || gtmId.trim() === '') return;

	// Deliberately frozen at boot: plugins run once per FULL page load, and
	// that is the only case the shorts defer targets. On an internal (SPA)
	// navigation into /shorts, GTM already loaded on the hosting page's own
	// trigger, so there is nothing left to defer or beacon.
	const isShortsEntry = useRoute().path.startsWith('/shorts');
	let gtmRequested = false;

	const trigger = new Promise<void>(resolve => {
		onNuxtReady(() => {
			let settled = false;
			const cleanups: Array<() => void> = [];

			const fire = () => {
				if (settled) return;
				settled = true;
				gtmRequested = true;
				for (const undo of cleanups) undo();
				resolve();
			};

			const timer = setTimeout(
				fire,
				isShortsEntry ? SHORTS_EXTRA_DEFER_CAP_MS : DEFAULT_DEFER_CAP_MS
			);
			cleanups.push(() => clearTimeout(timer));

			if (isShortsEntry) {
				// First frame or first interaction, whichever wins the cap.
				window.addEventListener(SHORTS_FIRST_FRAME_EVENT, fire, {
					once: true
				});
				cleanups.push(() =>
					window.removeEventListener(SHORTS_FIRST_FRAME_EVENT, fire)
				);
				for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
					window.addEventListener(type, fire, { once: true, passive: true });
					cleanups.push(() => window.removeEventListener(type, fire));
				}
			} else if ('requestIdleCallback' in window) {
				// Guaranteed fallback: the timer above still fires in background
				// tabs (where requestIdleCallback can be starved), so GTM never
				// fails to load. requestIdleCallback just lets it fire earlier.
				requestIdleCallback(fire, { timeout: DEFAULT_DEFER_CAP_MS });
			}
		});
	});

	if (isShortsEntry) {
		registerExitBeacon(() => gtmRequested);
	}

	useScriptGoogleTagManager({
		id: gtmId,
		scriptOptions: {
			trigger,
			warmupStrategy: false
		}
	});
});

/**
 * Exit before GTM landed: sends a minimal GA4 page_view via sendBeacon, which
 * outlives the tab by design, so fast bounces on shorts entries still count.
 * The client id persists in localStorage so a returning visitor (with GTM
 * loading normally) stays a single user in GA — gtag adopts nothing from
 * here, but counting one anonymous pageview beats counting nothing.
 */
function registerExitBeacon(wasGtmRequested: () => boolean): void {
	const measurementId = useRuntimeConfig().public.ga4MeasurementId as string;
	if (!measurementId || !('sendBeacon' in navigator)) return;

	const CID_KEY = 'bifrost_beacon_cid';
	let sent = false;

	const onPageHide = () => {
		if (sent || wasGtmRequested()) return;
		sent = true;
		try {
			let cid = localStorage.getItem(CID_KEY);
			if (!cid) {
				cid = `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
				localStorage.setItem(CID_KEY, cid);
			}
			const params = new URLSearchParams({
				v: '2',
				tid: measurementId,
				cid,
				en: 'page_view',
				dl: location.href,
				dt: document.title,
				_et: '1'
			});
			navigator.sendBeacon(
				`https://www.google-analytics.com/g/collect?${params.toString()}`
			);
		} catch {
			// Beacon is best-effort — never interfere with the unload.
		}
	};

	window.addEventListener('pagehide', onPageHide);
}
